/// <amd-module name="VSS/Diagnostics/Dialog/DiagnosticsContentsTree" />
import React = require("react");
import { IVssComponentProperties, VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { ContributionLink, ContributionNode } from "VSS/Diagnostics/Dialog/DiagnosticsDialog.types";
import { Filter, FILTER_CHANGE_EVENT } from "VSSUI/Utilities/Filter";
import { FilterBar } from "VSSUI/FilterBar";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Tree, ITreeProps, ITreeColumn, renderExpandableTreeCell, renderTreeCell } from "VSSUI/TreeEx";
import { ISimpleTableCell } from "VSSUI/Table";
import { TreeItemProvider, ITreeItem, ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { IVssContentService, ContentType } from "VSS/Platform/Content";

export interface PerfDiagnosticsTreeProps extends IVssComponentProperties {
    contributions: ContributionNode[];
    relationships: ContributionLink[];
}

interface IContributionItem extends ISimpleTableCell {
    cid: string;
    cjs: string;
    ccss: string;
}
export interface PerfDiagnosticsContentTreePivotState extends IVssComponentProperties {
    filter: Filter;
    items: ITreeItem<IContributionItem>[];
}

function createContribution(cid: string, cjs: string, ccss: string): ITreeItem<IContributionItem> {
    return {
        childItems: [],
        expanded: false,
        data: {
            cid,
            cjs,
            ccss
        }
    };
}

export class PerfDiagnosticsContentsTree extends VssComponent<PerfDiagnosticsTreeProps, PerfDiagnosticsContentTreePivotState> {
    constructor(props: PerfDiagnosticsTreeProps, context: IVssComponentContext) {
        super(props, context);

        const filter = new Filter();
        filter.subscribe(this.onFilterChanged, FILTER_CHANGE_EVENT);
        const items = this.getContributionTreeItems("");
        this.treeItemProvider = new TreeItemProvider<IContributionItem>(items);
        this.state = { filter, items };
        
        this.columns = [
            { id: "cid", width: 1000, name: "Contribution Id", renderCell: renderExpandableTreeCell },
            { id: "cjs", width: 500, name: "Scripts Size (k)", renderCell: renderTreeCell },
            { id: "ccss", width: 500, name: "CSS Size (k)", renderCell: renderTreeCell }
        ];
    }

    private treeItemProvider: TreeItemProvider<IContributionItem>;
    private columns: ITreeColumn<IContributionItem>[];
    
    public render(): JSX.Element {
        const { filter } = this.state;
        return (
            <div className="flex-grow flex-column diag-dialog-full-height-width">
                <FilterBar filter={filter}>
                    <KeywordFilterBarItem placeholder="Search by Contribution Id" filterItemKey="keyword" maxTextLength={50} />
                </FilterBar>
                <div className="diag-dialog-full-height-width v-scroll-auto">
                    <Tree
                        containerClassName="diag-dialog-full-height-width"
                        columns={this.columns}
                        itemProvider={this.treeItemProvider}
                        onToggle={this.onToggle}
                        scrollable={true}
                    />
                </div>
            </div>
        );
    }

    private onToggle = (event: React.SyntheticEvent<HTMLElement>, treeItem: ITreeItemEx<IContributionItem>) => {
        this.treeItemProvider.toggle(treeItem.underlyingItem);
    };

    private getSearchString = () => {
        let searchString = this.state.filter.getFilterItemValue<string>("keyword") || "";
        return searchString.trim().toLocaleLowerCase();
    };

    private onFilterChanged = () => {
        const searchString = this.getSearchString();
        let items: ITreeItem<IContributionItem>[] = [];
        const filteredItems = this.getContributionTreeItems(searchString);
        items = filteredItems;
        this.treeItemProvider = new TreeItemProvider<IContributionItem>(filteredItems);
        this.setState({ items });
    };

    private getContributionTreeItems(searchString: string): ITreeItem<IContributionItem>[] {
        const { contributions, relationships } = this.props;
        searchString = searchString.trim().toLocaleLowerCase();
        const contentContributionType = "ms.vss-web.content";
        const filteredContributions = contributions.filter(x => x.type === contentContributionType);
        const contentService = this.context.pageContext.getService<IVssContentService>("IVssContentService");
        const cjss: { [key: string]: string } = {};
        const ccss: { [key: string]: string } = {};
        const cjssnum: { [key: string]: number } = {};

        const items: ITreeItem<IContributionItem>[] = [];
        const contributionsMap: { [id: string]: ContributionNode } = filteredContributions.reduce(
            (
                prev: {
                    [id: string]: ContributionNode;
                },
                contribution
            ) => {
                const cid = contribution.id;
                if (!cjss[cid]) {
                    const info = contentService.getContributionContentLengths(cid);
                    cjss[cid] = "";
                    ccss[cid] = "";
                    cjssnum[cid] = 0;
                    if (info) {
                        if (info[ContentType.javaScript]) {
                            cjss[cid] = (info[ContentType.javaScript] / 1000).toFixed(2);
                            cjssnum[cid] = info[ContentType.javaScript];
                        }
                        if (info[ContentType.css]) {
                            ccss[cid] = (info[ContentType.css] / 1000).toFixed(2);
                        }
                    }
                }
                prev[cid] = contribution;
                return prev;
            },
            {}
        );
        const parentsByChild: { [key: string]: ContributionLink[] } = relationships.reduce(
            (prev: { [childId: string]: ContributionLink[] }, relationship) => {
                const childId = relationship.childContributionId;
                const parentId = relationship.parentContributionId;
                if (contributionsMap[childId] && contributionsMap[parentId]) {
                    if (!prev[childId]) {
                        prev[childId] = [];
                    }
                    prev[childId].push(relationship);
                }
                return prev;
            },
            {}
        );
        const addConsumers = (item: ITreeItem<IContributionItem>) => {
            const cid = item.data.cid;
            const parentRelationships = parentsByChild[cid] || [];
            const childItems = parentRelationships
                .map(r => {
                    const pid = r.parentContributionId;
                    return createContribution(pid, cjss[pid], ccss[pid]);
                })
                .sort((a, b) => cjssnum[b.data.cid] - cjssnum[a.data.cid]);

            item.childItems!.push(...childItems);
            childItems.forEach(addConsumers);
        };
        const queue = searchString
            ? Object.keys(contributionsMap).filter((contributionId: string) => contributionId.toLocaleLowerCase().indexOf(searchString) >= 0)
            : Object.keys(contributionsMap);

        // sort descending on js size
        queue.sort((a, b) => cjssnum[b] - cjssnum[a]);
        // do bfs to find all included contributions that lead to finalChildIds
        while (queue.length > 0) {
            const cid = queue.shift()!;
            const item = createContribution(cid, cjss[cid], ccss[cid]);
            items.push(item);

            addConsumers(item);
        }
        return items;
    }
}
